import type { TFunction } from 'i18next';
import type { IObservableArray } from 'mobx';
import numeral from 'numeral';

import type { Targetable, Targetables, Targeting } from '@feathr/blackbox';
import { TargetableClass } from '@feathr/blackbox';
import type { ISliderProps } from '@feathr/components';

// Actually 10,000,000 after logarithmic scale
export const SLIDER_MAX_VALUE = 100;
// Actually 10,000 after logarithmic scale
export const SLIDER_MIN_VALUE = 0;
export const SLIDER_DEFAULT_VALUE = SLIDER_MIN_VALUE;

const SLIDER_ACTUAL_MAX_VALUE = 10_000_000;
const SLIDER_ACTUAL_MIN_VALUE = 10_000;

/**
 * Converts a slider position (0-100) to an actual audience size value (10,000 - 10,000,000).
 * Uses logarithmic scaling to provide better distribution across the range.
 * @param sliderPosition - The slider position (0-100)
 * @returns The actual audience size value
 */
export function sliderToValue(sliderPosition: number): number {
  const logMin = Math.log10(SLIDER_ACTUAL_MIN_VALUE);
  const logMax = Math.log10(SLIDER_ACTUAL_MAX_VALUE);
  const logValue = logMin + (sliderPosition / 100) * (logMax - logMin);
  return Math.round(10 ** logValue);
}

/**
 * Converts an actual audience size value to a slider position (0-100).
 * Inverse of sliderToValue, using logarithmic scaling.
 * @param actualValue - The actual audience size value
 * @returns The slider position (0-100)
 */
export function valueToSlider(actualValue: number): number {
  const logMin = Math.log10(SLIDER_ACTUAL_MIN_VALUE);
  const logMax = Math.log10(SLIDER_ACTUAL_MAX_VALUE);
  const logValue = Math.log10(actualValue);
  return Math.round(((logValue - logMin) / (logMax - logMin)) * 100);
}

/**
 * Rounds an audience size value to a clean number for display.
 * - Values under 100k: rounds to nearest 1,000
 * - Values 100k - 1M: rounds to nearest 10,000
 * - Values 1M+: rounds to nearest 100,000
 * @param value - The value to round
 * @returns The rounded value
 */
export function roundValue(value: number): number {
  if (value < 100_000) {
    return Math.round(value / 1_000) * 1_000;
  } else if (value < 1_000_000) {
    return Math.round(value / SLIDER_ACTUAL_MIN_VALUE) * SLIDER_ACTUAL_MIN_VALUE;
  } else {
    return Math.round(value / 100_000) * 100_000;
  }
}

/**
 * Formats a slider position as a human-readable tick label.
 * Values under 1M display as "100k", values 1M+ display as "1.0m".
 * @param sliderPosition - The slider position (0-100)
 * @returns Formatted string like "100k" or "1.0m"
 */
export function onFormatTickLabel(sliderPosition: number): string {
  const actualValue = sliderToValue(sliderPosition);
  const roundedValue = roundValue(actualValue);

  if (roundedValue >= 1_000_000) {
    // Shows 4.1m
    return numeral(roundedValue).format('0.0a');
  }
  // Shows 100k
  return numeral(roundedValue).format('0a');
}

/**
 * Returns the slider marks for the min and max positions.
 * @param t - Translation function
 * @returns Array of slider marks with translated labels
 */
export function getSliderMarks(t: TFunction): ISliderProps['marks'] {
  return [
    { value: SLIDER_MIN_VALUE, label: t('10,000') },
    { value: SLIDER_MAX_VALUE, label: t('10 million') },
  ];
}

/**
 * Creates a new lookalike targetable with the given audience size.
 * @param Targetables - The Targetables store instance
 * @param sliderPosition - The slider position (0-100), defaults to SLIDER_DEFAULT_VALUE
 */
export function createLookalikeTargetable(
  Targetables: Targetables,
  sliderPosition: number = SLIDER_DEFAULT_VALUE,
): Targetable {
  const actualValue = sliderToValue(sliderPosition);
  return Targetables.create({
    _cls: TargetableClass.omni_lookalike,
    audience_size: actualValue,
    name: 'Lookalike Audience',
  });
}

/**
 * Finds an enabled (non-archived) lookalike targeting for the given campaign.
 * @param targetings - Observable array of targetings to search
 * @param campaignId - The campaign ID to match against the targeting's parent
 * @returns The matching targeting, or undefined if not found
 */
export function findEnabledLookalikeTargeting(
  targetings: IObservableArray<Targeting>,
  campaignId: string,
): Targeting | undefined {
  return targetings.find((targeting) => {
    const kind = targeting.get('kind');
    const parent = targeting.get('parent');
    const isArchived = targeting.get('is_archived');
    return kind === 'omni_lookalike' && parent === campaignId && isArchived === false;
  });
}

/**
 * Finds an archived lookalike targeting for the given campaign.
 * Used when re-enabling lookalike to check if we can reuse an existing targeting.
 * @param targetings - Observable array of targetings to search
 * @param campaignId - The campaign ID to match against the targeting's parent
 * @returns The matching archived targeting, or undefined if not found
 */
export function findArchivedLookalikeTargeting(
  targetings: IObservableArray<Targeting>,
  campaignId: string,
): Targeting | undefined {
  return targetings.find((targeting) => {
    const kind = targeting.get('kind');
    const parent = targeting.get('parent');
    const isArchived = targeting.get('is_archived');
    return kind === 'omni_lookalike' && parent === campaignId && isArchived === true;
  });
}
